/* =========================================================================
   Loading tile preferences — per device.

   The loading tile is a content moment, not a spinner: it carries a quote or
   a memory from the user's own journal and holds it for a readable dwell.
   Not everyone wants the moment; some visits are just a page you need. These
   two switches let a quote or a memory be turned off in the instant it
   appears, and turned back on from /my-account/preferences.

   Device-local on purpose. This says how fast this browser should feel, not
   something true about the account, so it follows the existing nwd_*
   localStorage convention (FeedbackBanner.jsx, DonationBanner.jsx) rather
   than adding a field to the user model. The cost is that clearing site data
   resets it, which the settings page says out loud.
   ========================================================================= */

import { useEffect, useState } from "react";

export const LOADING_PREFS_KEY = "nwd_loading_tiles";

/* Same-tab change notification. `storage` fires only in OTHER tabs, so a tile
   on screen while the settings page flips a switch would never hear about it
   without this. */
const PREFS_EVENT = "nwd:loading-prefs";

/* On by default. The tile is the designed experience and opting out is the
   choice — so a blocked or cleared store has to read as "on", never as "off".
   That is also why every access below is guarded rather than assumed. */
const DEFAULTS = Object.freeze({ quotes: true, memories: true });

/* Tile type -> the preference that governs it. Only these two are optional;
   welcome and milestone tiles are earned celebrations, not filler, and are
   deliberately absent from this map. */
export const TILE_KINDS = Object.freeze({
  quote: "quotes",
  memory: "memories",
});

export const readLoadingPrefs = () => {
  if (typeof window === "undefined") return { ...DEFAULTS };
  try {
    const raw = window.localStorage.getItem(LOADING_PREFS_KEY);
    if (!raw) return { ...DEFAULTS };
    const stored = JSON.parse(raw);
    return {
      quotes:
        typeof stored?.quotes === "boolean" ? stored.quotes : DEFAULTS.quotes,
      memories:
        typeof stored?.memories === "boolean"
          ? stored.memories
          : DEFAULTS.memories,
    };
  } catch {
    return { ...DEFAULTS };
  }
};

export const setLoadingPref = (kind, enabled) => {
  const next = { ...readLoadingPrefs(), [kind]: Boolean(enabled) };
  if (typeof window === "undefined") return next;
  try {
    window.localStorage.setItem(LOADING_PREFS_KEY, JSON.stringify(next));
  } catch {
    /* Storage blocked. The event below still carries the change, so the flip
       holds for this session — it just will not survive a reload. Better than
       a switch that appears to do nothing. */
  }
  window.dispatchEvent(new CustomEvent(PREFS_EVENT, { detail: next }));
  return next;
};

/**
 * Live preferences, kept in step with flips made anywhere — this tab or
 * another. Use this where the UI must react (the settings page). Code that
 * must NOT react mid-read calls readLoadingPrefs() once instead.
 */
export const useLoadingPreferences = () => {
  const [prefs, setPrefs] = useState(readLoadingPrefs);

  useEffect(() => {
    const onLocal = (event) =>
      setPrefs(event?.detail ? { ...event.detail } : readLoadingPrefs());
    const onStorage = (event) => {
      if (event.key && event.key !== LOADING_PREFS_KEY) return;
      setPrefs(readLoadingPrefs());
    };

    window.addEventListener(PREFS_EVENT, onLocal);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(PREFS_EVENT, onLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return prefs;
};

/* =========================================================================
   Session state — not a preference.

   The backlog tile states how many days are still waiting for a feeling
   score. It is shown at most once per session: enough to flag the work, never
   enough to become the thing that replaces every quote for the rest of the
   visit. A journal that nags you about an unfinished day has misunderstood
   journaling (design.md §1.4).

   sessionStorage, not localStorage, and deliberately so: the tile must come
   back on the next visit rather than harden into a permanent hide. That is
   also why `backlog` is absent from TILE_KINDS above — there is no "don't
   show this" switch for it, because it is work rather than filler. Skip is
   the exit.
   ========================================================================= */

export const BACKLOG_TILE_KEY = "nwd_backlog_tile_shown";

export const hasSeenBacklogTile = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(BACKLOG_TILE_KEY) === "true";
  } catch (err) {
    // Storage blocked. Read as a fresh session: showing it once too often is
    // better than silently never flagging the backlog at all.
    return false;
  }
};

export const markBacklogTileSeen = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(BACKLOG_TILE_KEY, "true");
  } catch (err) {
    /* private mode — the tile may appear again on the next navigation */
  }
};
